/**
 * Global JavaScript Defer for Shopify
 * 
 * This script defers loading of non-critical JavaScript until:
 * 1. User interaction (mouse move, touch, scroll)
 * 2. OR after idle time
 * 3. OR when elements enter viewport
 * 
 * HOW TO IMPLEMENT:
 * 1. Add this code to your theme.liquid file just before the closing </body> tag
 * 2. Identify scripts to defer by adding the attribute 'data-defer="true"' to them
 * 3. For third-party scripts, you may need to modify app embed codes
 */

// Create a self-executing function to avoid global scope pollution
(function() {
  
    // Store references to original script loading methods
    const originalCreateElement = document.createElement.bind(document);
    const originalAppendChild = Element.prototype.appendChild;
    const originalInsertBefore = Element.prototype.insertBefore;
    
    // Store scripts to be loaded later
    const deferredScripts = [];
    let scriptsLoaded = false;
    
    // Add cache key for localStorage
    const CACHE_KEY = 'deferred_scripts_loaded';
    
    // Function to load all deferred scripts
    function loadDeferredScripts() {
      if (scriptsLoaded) return;
      scriptsLoaded = true;
      
      // Remove all event listeners
      document.removeEventListener('mousemove', triggerLoad);
      document.removeEventListener('touchstart', triggerLoad);
      document.removeEventListener('scroll', triggerLoad);
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
      clearTimeout(idleTimer);
      
      console.log('Loading deferred scripts...');
      
      // Process stored scripts
      deferredScripts.forEach(script => {
        loadScript(script);
      });
      
      // Restore original methods
      document.createElement = originalCreateElement;
      Element.prototype.appendChild = originalAppendChild;
      Element.prototype.insertBefore = originalInsertBefore;
      
      console.log('All deferred scripts loaded');
    }
    
    // Helper function to load individual script
    function loadScript(script) {
      try {
        const newScript = originalCreateElement('script');
        
        // Copy all attributes from the original script
        Array.from(script.attributes || []).forEach(attr => {
          if (attr.name !== 'data-defer') {
            newScript.setAttribute(attr.name, attr.value);
          }
        });
        
        // Copy inline script content if any
        if (script.innerHTML) {
          newScript.innerHTML = script.innerHTML;
        }
        
        // Add the script to the document
        document.body.appendChild(newScript);
      } catch (error) {
        console.error('Error loading script:', error);
      }
    }
    
    // Create trigger function for user interaction
    function triggerLoad() {
      loadDeferredScripts();
    }
    
    // Set up intersection observer for viewport detection
    let intersectionObserver;
    
    // Set timer for idle loading (reduced from 15s to 3s)
    const idleTimer = setTimeout(loadDeferredScripts, 3000);
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
      initialize();
    }
    
    function initialize() {
      // Add user interaction listeners
      document.addEventListener('mousemove', triggerLoad, {once: true});
      document.addEventListener('touchstart', triggerLoad, {once: true});
      document.addEventListener('scroll', triggerLoad, {once: true});
      
      // Set up intersection observer
      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver((entries) => {
          if (entries.some(entry => entry.isIntersecting)) {
            loadDeferredScripts();
          }
        });
        
        // Observe the middle of the page and above the fold elements
        setTimeout(() => {
          // Observe key elements that might trigger script loading
          const observeElements = [
            document.querySelector('header'),
            document.querySelector('main'),
            document.querySelector('.product-section'),
            document.querySelector('.collection-list')
          ].filter(Boolean);
          
          if (observeElements.length) {
            observeElements.forEach(el => intersectionObserver.observe(el));
          } else {
            // Fallback to middle element if no key elements found
            const allElements = document.querySelectorAll('body *');
            if (allElements.length) {
              const middleElement = allElements[Math.floor(allElements.length / 2)];
              intersectionObserver.observe(middleElement);
            }
          }
        }, 500);
      }
      
      // Override document.createElement to intercept script creation
      document.createElement = function(tagName) {
        const element = originalCreateElement(tagName);
        
        if (tagName.toLowerCase() === 'script') {
          // Mark as potentially deferrable
          element._isDeferrable = true;
        }
        
        return element;
      };
      
      // Override appendChild to catch script insertions
      Element.prototype.appendChild = function(element) {
        // Check if this is a script element that should be deferred
        if (element && element.tagName && 
            element.tagName.toLowerCase() === 'script' && 
            !scriptsLoaded && 
            (element.hasAttribute('data-defer') || 
             (element._isDeferrable && !element.hasAttribute('data-critical')))) {
          
          // Store for later loading
          deferredScripts.push(element);
          return element; // Return as if it was appended
        }
        
        // Default behavior for other elements
        return originalAppendChild.call(this, element);
      };
      
      // Override insertBefore to catch script insertions
      Element.prototype.insertBefore = function(element, referenceNode) {
        // Check if this is a script element that should be deferred
        if (element && element.tagName && 
            element.tagName.toLowerCase() === 'script' && 
            !scriptsLoaded && 
            (element.hasAttribute('data-defer') || 
             (element._isDeferrable && !element.hasAttribute('data-critical')))) {
          
          // Store for later loading
          deferredScripts.push(element);
          return element; // Return as if it was inserted
        }
        
        // Default behavior for other elements
        return originalInsertBefore.call(this, element, referenceNode);
      };
    }
    
    // Log setup confirmation
    console.log('Global script deferring initialized');
})();